import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/** Unique LabTrack ID input with optional auto-generation and duplicate-patient warning. */
export const UlidField = ({ field, value, onChange, patientName = "" }) => {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState("");

  useEffect(() => {
    api.get("/ulid/settings").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  useEffect(() => {
    const ulid = String(value || "").trim();
    if (!ulid) { setWarning(""); return; }
    const t = setTimeout(() => {
      api.get("/patients/check", { params: { ulid, name: patientName } })
        .then(({ data }) => setWarning(data.status === "mismatch" ? data.message : ""))
        .catch(() => setWarning(""));
    }, 500);
    return () => clearTimeout(t);
  }, [value, patientName]);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ulid/next");
      onChange(data.ulid);
      toast.success(`ULID ${data.ulid} generated`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="mt-1.5">
      <div className="flex gap-1.5">
        <Input value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={`input-${field.name}`} placeholder="Patient ULID, e.g. LTBP-ABC-2608270001" />
        {cfg?.enabled && (
          <Button type="button" variant="outline" onClick={generate} disabled={busy}
            data-testid={`generate-ulid-${field.name}`} title="Generate a new ULID">
            <Sparkles className="h-4 w-4 text-[#002FA7]" />
          </Button>
        )}
      </div>
      {warning ? (
        <p className="text-[11px] text-[#B45309] mt-1" data-testid={`ulid-warning-${field.name}`}>
          {warning}
        </p>
      ) : field.hint ? (
        <p className="text-[10px] text-[#6B7280] mt-1">{field.hint}</p>
      ) : null}
    </div>
  );
};
